import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import HomePage from "../pages/HomePage";
import { loadHomePageV2 } from "../pages/homeV2/loadHomePageV2";
import configService from "../services/configService";
import trackingService from "../services/trackingService";
import { homepageFallbackWasShown } from "../utils/homepageStartup";
import { readLastLoginPreference } from "../utils/lastLoginPreference";
import PlutusStartupScreen from "./PlutusStartupScreen";

export const HOMEPAGE_EXPERIMENT_KEY = "homepage-v2-signin";
export const FLAG_WAIT_MS = 1000;
export const V2_LOAD_WAIT_MS = 15000;
type HomepageDecision = { variant: "control" | "home-v2" | null };
type V2Load = { status: "loading" | "error" } | {
  status: "ready";
  Page: React.ComponentType<{ analyticsEnabled?: boolean }>;
};

export function isHomepageExperimentLocation(hostname: string, pathname: string, production: boolean): boolean {
  return production && pathname === "/" && ["www.runplutus.com", "runplutus.com"].includes(hostname);
}

const BaseRouteHandler: React.FC = () => {
  const { isAuthenticated, isLoading, getDefaultLandingPage } = useAuth();
  const { pathname } = useLocation();
  const [decision, setDecision] = useState<HomepageDecision | null>(() => homepageFallbackWasShown() ? { variant: null } : null);
  const decided = useRef(decision);
  const [v2, setV2] = useState<V2Load>({ status: "loading" });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const pageMounted = useRef(homepageFallbackWasShown());
  const pageRef = useRef<HTMLDivElement>(null);
  const seen = useRef(false);
  const eligible = !isLoading && !isAuthenticated
    && isHomepageExperimentLocation(window.location.hostname, pathname, configService.isProduction())
    && readLastLoginPreference() === null && trackingService.canEvaluateAnonymousFlags();
  const session = useRef({ isAuthenticated, isLoading, eligible });
  session.current = { isAuthenticated, isLoading, eligible };

  useEffect(() => {
    if (decided.current || isLoading) return;
    const controller = new AbortController();
    let timeout: number | undefined;
    let unsubscribe: (() => void) | undefined;
    const stop = () => {
      window.clearTimeout(timeout);
      unsubscribe?.();
      unsubscribe = undefined;
    };
    const commit = (variant: HomepageDecision["variant"]) => {
      if (controller.signal.aborted || decided.current) return;
      const next = { variant };
      decided.current = next;
      stop();
      setDecision(next);
    };
    if (!eligible) {
      commit(null);
      return;
    }
    const select = (errorsLoading = false) => {
      if (controller.signal.aborted || decided.current) return;
      if (!session.current.eligible || !trackingService.canEvaluateAnonymousFlags() || readLastLoginPreference() !== null) {
        commit(null);
        return;
      }
      if (errorsLoading) { commit(null); return; }
      const variant = trackingService.readFeatureFlag(HOMEPAGE_EXPERIMENT_KEY, true);
      if (variant === undefined) return;
      commit(variant === "control" || variant === "home-v2" ? variant : null);
    };

    // Cached assignment only warms the chunk. A fresh server value must select it.
    if (trackingService.readFeatureFlag(HOMEPAGE_EXPERIMENT_KEY) === "home-v2") {
      void loadHomePageV2().catch(() => undefined);
    }
    timeout = window.setTimeout(() => commit(null), FLAG_WAIT_MS);
    unsubscribe = trackingService.onFeatureFlags(select);
    // The SDK can invoke its callback synchronously while subscribing.
    if (decided.current) stop();
    else select();
    return () => { controller.abort(); stop(); };
  }, [eligible, isLoading]);

  useEffect(() => {
    if (decision?.variant !== "home-v2" || pageMounted.current || isLoading || isAuthenticated) return;
    const controller = new AbortController();
    let settled = false;
    const current = () => !controller.signal.aborted && !settled
      && !session.current.isLoading && !session.current.isAuthenticated;
    const fail = () => {
      if (!current()) return;
      settled = true;
      setV2({ status: "error" });
    };
    setV2({ status: "loading" });
    // Assignment is already frozen. A chunk failure must never select control.
    const timeout = window.setTimeout(fail, V2_LOAD_WAIT_MS);
    void loadHomePageV2().then(module => {
      if (!current()) return;
      settled = true;
      window.clearTimeout(timeout);
      setV2({ status: "ready", Page: module.default });
    }).catch(() => {
      window.clearTimeout(timeout);
      fail();
    });
    return () => { controller.abort(); window.clearTimeout(timeout); };
  }, [decision, isLoading, isAuthenticated, loadAttempt]);

  // Only a V2 that has actually mounted owns its post-sign-in Buy/alerts flow.
  const redirect = !isLoading && isAuthenticated && !(decision?.variant === "home-v2" && pageMounted.current);
  const showPage = decision !== null && !redirect && (!isLoading || pageMounted.current)
    && (decision.variant !== "home-v2" || v2.status === "ready");

  useLayoutEffect(() => {
    if (showPage) pageMounted.current = true;
  }, [showPage]);

  useEffect(() => {
    if (!showPage || isLoading || isAuthenticated || seen.current) return;
    let frame = 0;
    let cancelled = false;
    const record = () => {
      if (cancelled || seen.current || document.visibilityState !== "visible" || !pageRef.current?.isConnected
        || document.documentElement.getAttribute("data-homepage-startup") === "pending") return;
      seen.current = true;
      if (decision?.variant && session.current.eligible && trackingService.canEvaluateAnonymousFlags()) {
        trackingService.recordFeatureFlagExposure(HOMEPAGE_EXPERIMENT_KEY, decision.variant);
      }
      setAnalyticsEnabled(true);
    };
    const afterPaint = () => {
      window.cancelAnimationFrame(frame);
      if (document.visibilityState !== "visible") return;
      // Two frames allow the committed page to paint before exposure/impressions.
      frame = window.requestAnimationFrame(() => { frame = window.requestAnimationFrame(record); });
    };
    document.addEventListener("visibilitychange", afterPaint);
    afterPaint();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", afterPaint);
    };
  }, [showPage, isLoading, isAuthenticated, decision]);

  if (redirect) return <Navigate to={getDefaultLandingPage("homepage")} replace />;
  if (!showPage) {
    return <PlutusStartupScreen>
      {decision?.variant === "home-v2" && v2.status === "error" && <>
        <p role="alert">We could not load this page. Check your connection and try again.</p>
        <button type="button" className="mt-4 rounded-lg bg-rp-amber px-4 py-2 font-semibold text-white transition-colors hover:bg-yellow-600"
          onClick={() => { setV2({ status: "loading" }); setLoadAttempt(value => value + 1); }}>Try again</button>
        <button type="button" className="ml-3 rounded-lg border border-gray-200 px-4 py-2 text-sm text-white"
          onClick={() => window.location.reload()}>Reload page</button>
      </>}
    </PlutusStartupScreen>;
  }
  return <div ref={pageRef} inert={isLoading} aria-busy={isLoading}>
    {decision?.variant === "home-v2" && v2.status === "ready"
      ? <v2.Page analyticsEnabled={analyticsEnabled} />
      : <HomePage analyticsEnabled={analyticsEnabled} />}
  </div>;
};

export default BaseRouteHandler;
